/**
 * Schedule a Demo Modal — TestOps Command
 * Mirrors apps/{toucan,athena}/web/src/components/DemoModal.tsx so the form
 * fields and submission behaviour are consistent across product marketing pages.
 */
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Send, Mail, User, Building, Calendar, Clock, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react'

interface DemoModalProps {
  isOpen: boolean
  onClose: () => void
}

const IDENTITY_API_URL = import.meta.env.VITE_IDENTITY_API_URL || 'https://api.ytest.ai'

export function DemoModal({ isOpen, onClose }: DemoModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    jobTitle: '',
    preferredDate: '',
    preferredTime: '',
    timezone: 'America/New_York',
    sapEnvironment: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitResult(null)

    try {
      const response = await fetch(`${IDENTITY_API_URL}/api/v1/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...formData, product: 'TestOps Command' }),
      })

      if (response.ok) {
        setSubmitResult({ success: true, message: 'Demo scheduled! We\'ll send a calendar invite to your email shortly.' })
        setFormData({
          name: '', email: '', company: '', jobTitle: '',
          preferredDate: '', preferredTime: '', timezone: formData.timezone,
          sapEnvironment: '', message: ''
        })
      } else {
        const error = await response.json()
        setSubmitResult({ success: false, message: error.detail || 'Failed to schedule demo. Please try again.' })
      }
    } catch (err) {
      setSubmitResult({ success: false, message: 'Failed to schedule demo. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'
  ]

  const useCases = [
    'Test Orchestration & AI Ops',
    'Cross-tool integration (Jira/GitHub/etc.)',
    'Browser/Web Automation',
    'SAP/Enterprise Testing',
    'API & Service Testing',
    'Other / General Test Ops'
  ]

  // Min selectable date is tomorrow — same constraint as Toucan/Athena modals.
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const minDate = tomorrow.toISOString().split('T')[0]

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-lg bg-card rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="bg-accent px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Schedule a Demo</h2>
                  <p className="text-white/80 text-sm mt-1">See TestOps Command orchestrate your test stack</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {submitResult && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl flex items-center gap-3 ${
                    submitResult.success
                      ? 'bg-success-soft text-success-foreground border border-success/30'
                      : 'bg-destructive-soft text-destructive-foreground border border-destructive/30'
                  }`}
                >
                  {submitResult.success ? (
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  )}
                  <span className="text-sm">{submitResult.message}</span>
                </motion.div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle-foreground" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full pl-10 pr-4 py-2.5 bg-input text-foreground border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Work Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle-foreground" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-input text-foreground border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Company *</label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle-foreground" />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      placeholder="Your company"
                      className="w-full pl-10 pr-4 py-2.5 bg-input text-foreground border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
                    />
                  </div>
                </div>

                {/* Job Title */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Job Title</label>
                  <input
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    placeholder="QA Manager"
                    className="w-full px-4 py-2.5 bg-input text-foreground border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
                  />
                </div>
              </div>

              {/* Use Case (reuses sapEnvironment field name to match shared API) */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Primary Use Case *</label>
                <select
                  name="sapEnvironment"
                  value={formData.sapEnvironment}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 bg-input text-foreground border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
                >
                  <option value="">Select your primary use case</option>
                  {useCases.map(uc => (
                    <option key={uc} value={uc}>{uc}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Preferred Date */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Preferred Date *</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle-foreground" />
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      required
                      min={minDate}
                      className="w-full pl-10 pr-4 py-2.5 bg-input text-foreground border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
                    />
                  </div>
                </div>

                {/* Preferred Time */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Preferred Time (EST) *</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle-foreground" />
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-2.5 bg-input text-foreground border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring"
                    >
                      <option value="">Select time</option>
                      {timeSlots.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                All times are in Eastern Standard Time (EST)
              </p>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Anything specific you'd like to see? (Optional)
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-subtle-foreground" />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="E.g., Jira + GitHub orchestration, AI test ops on our existing stack..."
                    className="w-full pl-10 pr-4 py-2.5 bg-input text-foreground border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring resize-none"
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-accent hover:bg-accent/90 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-accent/30 disabled:bg-disabled disabled:text-disabled-foreground disabled:shadow-none disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Schedule Demo
                  </>
                )}
              </button>

              <p className="text-xs text-muted-foreground text-center">
                We'll send a calendar invite with a meeting link to your email
              </p>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default DemoModal
